import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { agentKeys } from 'features/agents/hooks';
import { decideApproval, getApproval, requestBranchReview, retryApproval } from '../../approvals/api';
import { approvalRefetchInterval } from '../../approvals/approvalPolling';
import { publicationError } from '../../approvals/publicationError';
import { isAutomationBranchApproval, isBranchReviewApproval } from '../../approvals/branchReviewScope';

const matchesRevision = (approval, context) => isBranchReviewApproval(approval)
  && approval.scope?.sandbox_id === context.branchId
  && approval.scope?.overlay_id === context.revisionId
  && approval.scope?.overlay_hash === context.contentHash;

/** One explicit user click, using the existing authenticated approval lifecycle. */
export function useBranchPublication({ branch, review, context, ready, emptyReview = false, onPublished }) {
  const queryClient = useQueryClient();
  const [submittedId, setSubmittedId] = useState(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const approvalId = context.approvalId || submittedId
    || (review?.publication?.kind === 'sandbox_publication' ? review.publication.approval_id : null);
  const query = useQuery({
    queryKey: ['approval', approvalId],
    queryFn: () => getApproval(approvalId),
    enabled: Boolean(ready && approvalId),
    refetchInterval: approvalRefetchInterval,
    refetchIntervalInBackground: true,
  });
  const approval = query.data?.approval;
  const automation = isAutomationBranchApproval(approval);
  const closed = ['published', 'archived', 'discarded'].includes(branch?.sandbox_status);
  const workingReview = Boolean(approvalId && review?.working_review_id === approvalId && !review.is_committed);
  const matchesWorkingReview = (request) => workingReview && isBranchReviewApproval(request) && request.id === approvalId
    && request.scope?.sandbox_id === context.branchId && request.scope?.overlay_id === review?.head_revision_id;
  const valid = Boolean(ready && (review?.is_committed || workingReview) && review?.validation?.valid === true && context.contentHash);
  const ownDecision = query.data?.decisions?.some((row) => row.reviewer_user_profile_id === query.data?.reviewer_user_profile_id);
  const matches = !approval || matchesRevision(approval, context) || matchesWorkingReview(approval);
  const completed = approval?.status === 'approved'
    && (automation ? ['not_required', 'succeeded'].includes(approval.execution_state) : approval.execution_state === 'succeeded');
  const running = ['pending', 'running'].includes(approval?.execution_state) && approval?.status === 'approved';
  const failed = approval?.status === 'approved' && approval.execution_state === 'failed';
  const canAct = valid && !closed && matches && !query.isError
    && (!approvalId || (!query.isLoading && approval && query.data.can_review
      && (failed || (approval.status === 'pending' && !ownDecision))));
  const finish = useCallback((request) => {
    if (!mounted.current || !matchesRevision(request, context)) return;
    if (isAutomationBranchApproval(request) && request.status === 'approved'
        && ['not_required', 'succeeded'].includes(request.execution_state)) {
      toast.success('Approval recorded. The automation can continue.');
    } else if (request.status === 'approved' && request.execution_state === 'succeeded') {
      toast.success(request.kind === 'ontology_schema_publication' ? 'Ontology changes are live.' : 'Changes are live.');
      onPublished?.();
    }
  }, [context.branchId, context.revisionId, context.contentHash, onPublished]);
  // A delayed effect may return to Live only with this revision's confirmed receipt.
  const notified = useRef(false);
  useEffect(() => {
    if (completed && !closed && matches && !notified.current) {
      notified.current = true;
      finish(approval);
      // The revision is Live now: branch previews, Agent state and Views may all change.
      void queryClient.invalidateQueries();
    }
  }, [completed, closed, matches, approval, finish, queryClient]);
  const mutation = useMutation({
    mutationFn: async () => {
      let id = approvalId;
      if (!id) {
        const submitted = await requestBranchReview(context.branchId, context.revisionId, context.contentHash);
        id = submitted.approval_ref || submitted.approval?.id;
        if (!id) throw new Error('The approval request was not returned. Try again.');
        if (!mounted.current) return null;
        setSubmittedId(id);
      }
      // Recheck authority and immutable scope, also after uncertain/lost responses.
      const current = await getApproval(id);
      queryClient.setQueryData(['approval', id], current);
      if (!mounted.current) return null;
      if (!matchesRevision(current.approval, context) && !matchesWorkingReview(current.approval)) throw new Error('This approval belongs to different changes. Reopen the proposal.');
      if (!current.can_review) return current;
      if (current.approval.status === 'approved') {
        return current.approval.execution_state === 'failed' ? retryApproval(id) : current;
      }
      if (current.approval.status !== 'pending') throw new Error('This approval request is closed. Review its details before continuing.');
      if (current.decisions?.some((row) => row.reviewer_user_profile_id === current.reviewer_user_profile_id)) return current;
      if (workingReview) return decideApproval(id, 'approved', null, undefined, {
        revision_id: context.revisionId, content_hash: context.contentHash,
      });
      return decideApproval(id, 'approved', null);
    },
    onSuccess: async (result) => {
      if (result?.approval) {
        const id = result.approval.id;
        queryClient.setQueryData(['approval', id], (previous) => ({ ...previous, ...result }));
        if (!notified.current && result.approval.status === 'approved'
            && (result.approval.execution_state === 'succeeded'
              || (isAutomationBranchApproval(result.approval) && result.approval.execution_state === 'not_required'))) {
          notified.current = true;
          finish(result.approval);
        }
        if (result.approval.execution_state === 'running') {
          // The publication runs after the decision returns. Refetching every page now
          // only competes with it for the same server; the receipt effect refreshes them.
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['approval', id] }),
            queryClient.invalidateQueries({ queryKey: agentKeys.inbox() }),
            queryClient.invalidateQueries({ queryKey: ['home'] }),
          ]);
          return;
        }
      }
      await queryClient.invalidateQueries();
    },
    onError: () => queryClient.invalidateQueries({ queryKey: ['approval'] }),
    onSettled: () => { inFlight.current = false; },
  });
  const busy = mutation.isPending || running;
  let label = automation ? 'Approve and continue'
    : approval?.kind === 'ontology_schema_publication' ? 'Approve and apply data structure changes' : 'Approve and apply';
  if (approval?.required_decisions > 1 && !ownDecision) label = 'Approve changes';
  if ((approval && !query.data?.can_review) || (ownDecision && approval?.status === 'pending')) label = 'Awaiting approval';
  if (failed) label = automation ? 'Retry continuation' : 'Retry applying changes';
  if (completed) label = automation ? 'Approved' : 'Live';
  if (closed && !completed) label = 'Review closed';
  if (busy) label = automation ? 'Continuing automation…' : 'Applying changes…';
  if (query.isError) label = 'Reload approval';
  const error = !matches ? 'This approval belongs to different changes. Reopen the proposal.'
    : query.error?.message || mutation.error?.message
      || (failed && (approval.execution_result?.error || (automation
        ? 'The automation could not continue. Your approval is saved.'
        : 'Changes could not be applied. Your approval is saved; retry applying them.')));
  const hint = emptyReview ? 'No changes to apply yet.'
    : !ready ? 'Loading the proposal…'
    : !review?.is_committed && !workingReview ? 'Ask your agent to save these changes before approving them.'
      : review?.validation?.valid !== true ? 'Resolve the issues found in these changes before approving them.'
        : approval && ['expired', 'cancelled', 'rejected'].includes(approval.status) ? 'This approval request is closed. See details.'
          : label === 'Awaiting approval' ? 'Waiting for the required reviewer decisions.' : null;
  return {
    label, busy, disabled: query.isError ? !ready || busy : !canAct || busy, error: publicationError(error), hint,
    publish: () => {
      if (query.isError) { void query.refetch(); return; }
      if (!canAct || busy || inFlight.current) return;
      inFlight.current = true;
      mutation.mutate();
    },
  };
}
